import { useEffect, useRef, useState } from 'preact/hooks';
import { resolveLocalizedString, t } from '../i18n';
import { openCartView } from '../navigation';
import { formatPrice } from '../utils/currency';
import { themeTokens } from '../themeTokens';
import type { CartWidgetSettings, CartIconType } from '../config';

export type CartWidgetType = 'icon' | 'compact' | 'summary' | 'auto';

const CART_ICON_PATHS: Record<CartIconType, string> = {
  'cart': 'M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z',
  'cart3': 'M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .49.598l-1 5a.5.5 0 0 1-.465.401l-9.397.472L4.415 11H13a.5.5 0 0 1 0 1H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM3.102 4l.84 4.479 9.144-.459L13.89 4H3.102zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z',
  'cart2': 'M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM3.102 4l1.313 7h8.17l1.313-7H3.102zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z',
  'cart-plus': 'M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM9 5.5a.5.5 0 0 0-1 0V7H6.5a.5.5 0 0 0 0 1H8v1.5a.5.5 0 0 0 1 0V8h1.5a.5.5 0 0 0 0-1H9V5.5z',
  'bag': 'M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1zm3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4h-3.5zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5z',
  'handbag': 'M8 1a2 2 0 0 1 2 2v2H6V3a2 2 0 0 1 2-2zm3 4V3a3 3 0 1 0-6 0v2H1.5a.5.5 0 0 0-.5.5v8a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-8a.5.5 0 0 0-.5-.5h-3.5zm-7 1h6v1a.5.5 0 0 1-1 0V6H5v1a.5.5 0 0 1-1 0V6zM2 6h2v1a1.5 1.5 0 0 0 3 0V6h2v1a1.5 1.5 0 0 0 3 0V6h2v7H2V6z',
  'basket': 'M5.757 1.071a.5.5 0 0 1 .172.686L3.383 6h9.234L10.07 1.757a.5.5 0 1 1 .858-.514L13.783 6H15a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h1.217L5.07 1.243a.5.5 0 0 1 .686-.172zM3.394 15l-1.48-6h-.97l1.525 6.426a.75.75 0 0 0 .729.574h9.604a.75.75 0 0 0 .73-.574L15.056 9h-.972l-1.479 6H3.394z',
  'basket2': 'M4 10a1 1 0 0 1 2 0v2a1 1 0 0 1-2 0v-2zm3 0a1 1 0 0 1 2 0v2a1 1 0 0 1-2 0v-2zm3 0a1 1 0 1 1 2 0v2a1 1 0 0 1-2 0v-2zM1.11 4.5 1 5v1h14V5l-.11-.5a.5.5 0 0 0-.46-.4H13.4l-3.3-3.3a.5.5 0 0 0-.7.7L12.1 4H3.9L6.6 1.4a.5.5 0 0 0-.7-.7l-3.3 3.3H1.57a.5.5 0 0 0-.46.4zM14 7v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7h12z',
  'shop': 'M2.97 1.35A1 1 0 0 1 3.73 1h8.54a1 1 0 0 1 .76.35l2.609 3.044A1.5 1.5 0 0 1 16 5.37v.255a2.375 2.375 0 0 1-4.25 1.458A2.371 2.371 0 0 1 9.875 8 2.37 2.37 0 0 1 8 7.083 2.37 2.37 0 0 1 6.125 8a2.37 2.37 0 0 1-1.875-.917A2.375 2.375 0 0 1 0 5.625V5.37a1.5 1.5 0 0 1 .361-.976zm1.78 4.275a1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0 1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0 1.375 1.375 0 1 0 2.75 0V5.37a.5.5 0 0 0-.12-.325L12.27 2H3.73L1.12 5.045A.5.5 0 0 0 1 5.37v.255a1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0zM1.5 8.5A.5.5 0 0 1 2 8h1a.5.5 0 0 1 .5.5v5h10v-5a.5.5 0 0 1 1 0v5.5a.5.5 0 0 1-.5.5H2a.5.5 0 0 1-.5-.5z',
};

const CartIcon = ({ size = 20, iconType = 'cart' }: { size?: number; iconType?: CartIconType }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d={CART_ICON_PATHS[iconType] ?? CART_ICON_PATHS.cart} />
  </svg>
);

function firstColor(...values: unknown[]): string {
  for (const value of values) {
    const color = typeof value === 'string' ? value.trim() : '';
    if (color) return color;
  }
  return '';
}

interface CartWidgetProps {
  count: number;
  total: number;
  variant: 'mobile' | 'desktop';
  type?: CartWidgetType;
  onCartChange: () => void;
  settings?: CartWidgetSettings;
}

export function CartWidget({ count, total, variant, type = 'summary', settings }: CartWidgetProps) {
  const iconType = settings?.icon_type ?? 'cart';
  const rootRef = useRef<HTMLElement | null>(null);
  const [hostWidth, setHostWidth] = useState<number>(0);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 1023px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 1023px)');
    const sync = () => setIsMobileViewport(mq.matches);
    sync();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  useEffect(() => {
    if (variant !== 'desktop' || type !== 'auto') return;
    const el = rootRef.current;
    const host = el?.parentElement;
    if (!host) return;
    const measure = () => setHostWidth(host.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    return () => ro.disconnect();
  }, [variant, type]);

  const openCart = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    const close = document.querySelector<HTMLButtonElement>('.sw-cart-drawer-layer:not(.is-closing) .btn-close');
    if (close) {
      close.click();
      return;
    }
    openCartView();
  };

  const resolveDesktopType = (): Exclude<CartWidgetType, 'auto'> => {
    if (type !== 'auto') return type;
    // Dual header mount uses the desktop slot on mobile too — keep icon-only there.
    if (isMobileViewport) return 'icon';
    if (hostWidth <= 0) return 'compact';
    if (hostWidth > 0 && hostWidth < 128) return 'icon';
    if (hostWidth > 0 && hostWidth < 220) return 'compact';
    return 'summary';
  };

  // Mobile viewport: always icon in header/bar (no price). Desktop keeps configured type.
  const resolvedType: Exclude<CartWidgetType, 'auto'> = type === 'auto'
    ? (variant === 'mobile' ? 'icon' : resolveDesktopType())
    : (isMobileViewport ? 'icon' : type);

  if (resolvedType === 'icon') {
    const showBadge = settings?.icon?.show_badge !== false;
    return (
      <a ref={rootRef as any} href="#" onClick={openCart} class="sw-cart-icon nav-link d-flex align-items-center" aria-label={t('cartWidget.cart')} style="display:inline-flex;align-items:center;justify-content:center;min-height:44px;height:44px;padding:0 .75rem;margin:0;line-height:1;box-sizing:border-box;color:var(--sw-header-links, var(--sw-primary));text-decoration:none;overflow:visible">
        <span class="sw-cart-icon-wrap" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 20px', alignSelf: 'center', verticalAlign: 'middle', width: '20px', height: '20px', minWidth: '20px', minHeight: '20px', maxWidth: '20px', maxHeight: '20px', padding: 0, margin: 0, lineHeight: 0, fontSize: 0, color: 'inherit', overflow: 'visible', boxSizing: 'content-box' }}>
          <span style={{ display: 'block', width: '20px', height: '20px', lineHeight: 0 }}>
            <CartIcon size={20} iconType={iconType} />
          </span>
          {showBadge && count > 0 && (
            <span class="sw-cart-badge" style={{ position: 'absolute', top: '-6px', right: '-8px', bottom: 'auto', left: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', minWidth: '18px', height: '18px', lineHeight: 1, padding: '0 5px', boxSizing: 'border-box', borderRadius: '999px', background: themeTokens.cartBadgeBg, color: themeTokens.cartBadgeText, textAlign: 'center', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
              {count}
            </span>
          )}
        </span>
      </a>
    );
  }

  if (resolvedType === 'compact') {
    const showPrice = settings?.compact?.show_price !== false;
    const showCount = settings?.compact?.show_count !== false;
    return (
      <a ref={rootRef as any} href="#" onClick={openCart} class={`sw-cart-compact${variant === 'mobile' ? ' nav-link' : ''}`} style="display:inline-flex;align-items:center;justify-content:flex-start;gap:0.5rem;width:100%;min-height:inherit;box-sizing:border-box;color:var(--sw-header-links, var(--sw-primary));text-decoration:none;font-size:0.9rem">
        <span style={{ color: 'inherit', display: 'inline-flex' }}><CartIcon size={18} iconType={iconType} /></span>
        {showPrice && <span style={{ fontWeight: 600, color: 'inherit' }}>{formatPrice(total)}</span>}
        {showCount && count > 0 && <span style={{ color: 'inherit' }}>({count})</span>}
      </a>
    );
  }

  // Summary mode — same rendering rules on desktop/mobile.
  const sumShowTitle = settings?.summary?.show_title !== false;
  const sumShowPrice = settings?.summary?.show_price !== false;
  const sumShowCount = settings?.summary?.show_count !== false;
  const sumTitle = resolveLocalizedString(settings?.summary?.titles, t('cartWidget.shoppingCart'));
  const sumButtonText = resolveLocalizedString(settings?.summary?.button_texts, t('cartWidget.viewCart'));
  const summaryButtonBg = firstColor(
    settings?.summary?.button_bg,
    settings?.summary?.button_background_color,
    settings?.summary?.button_color
  ) || 'var(--sw-primary, #0d6efd)';
  const summaryButtonText = firstColor(
    settings?.summary?.button_text_color,
    settings?.summary?.button_text_colour,
    settings?.summary?.text_color
  ) || '#fff';
  return (
    <div
      ref={rootRef as any}
      class="sw-cart-summary"
      style={{
        '--sw-cart-summary-button-bg': summaryButtonBg,
        '--sw-cart-summary-button-text': summaryButtonText,
      }}
    >
      {sumShowTitle && (
        <h5 class="sw-cart-summary__title">
          <span style="display:inline-flex;vertical-align:middle;margin-right:0.5rem"><CartIcon size={20} iconType={iconType} /></span>{sumTitle}
        </h5>
      )}
      <ul class="sw-cart-summary__list">
        {(sumShowPrice || sumShowCount) && (
          <li style="list-style: none;">
            {sumShowPrice && <span class="sw-cart-summary__price">{formatPrice(total)}</span>}
            {sumShowCount && <span class="sw-cart-summary__items">({count} {t('cartWidget.items')})</span>}
          </li>
        )}
        <li class="sw-cart-summary__action">
          <a
            href="#"
            onClick={openCart}
            class="sw-cart-summary__button"
            style={{
              '--sw-cart-summary-button-bg': summaryButtonBg,
              '--sw-cart-summary-button-text': summaryButtonText,
            }}
          >
            {sumButtonText}
          </a>
        </li>
      </ul>
    </div>
  );
}
